import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cc_user_token";

// dedicated axios instance so user auth never clashes with admin token header
const authApi = axios.create({ baseURL: BASE });

export const ROLES = [
  { value: "student", label: "Student" },
  { value: "counsellor", label: "Career Counsellor" },
  { value: "school", label: "School / College" },
];
export const roleLabel = (v) => (ROLES.find((r) => r.value === v) || {}).label || v;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));
  const authHeader = () => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const loadMe = async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return null;
    try {
      const r = await authApi.get("/auth/me", { headers: { Authorization: `Bearer ${t}` } });
      setUser(r.data.user);
      return r.data.user;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
  };

  useEffect(() => {
    const hash = window.location.hash || "";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      const role = localStorage.getItem("cc_pending_role") || "student";
      authApi
        .post("/auth/google", { session_id: sid, role })
        .then((r) => {
          setToken(r.data.token);
          setUser(r.data.user);
          localStorage.removeItem("cc_pending_role");
          window.history.replaceState(null, "", "/account");
        })
        .catch(() => {})
        .finally(() => setLoading(false));
      return;
    }
    loadMe().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const register = async (data) => {
    const r = await authApi.post("/auth/register", data);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const login = async (data) => {
    const r = await authApi.post("/auth/login", data);
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const googleSignIn = (role) => {
    localStorage.setItem("cc_pending_role", role || "student");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const forgot = (email) =>
    authApi.post("/auth/forgot", { email, base_url: window.location.origin });
  const reset = (token, password) => authApi.post("/auth/reset", { token, password });

  const get = (path) => authApi.get(path, { headers: authHeader() });

  return (
    <AuthContext.Provider
      value={{ user, loading, register, login, logout, googleSignIn, forgot, reset, authHeader, get }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
